import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../helpers/axiosInstance';
import Swal from 'sweetalert2';
import View from '../images/view.svg';
import Edit from '../images/edit.png';
import * as XLSX from "xlsx";
import Delete from "../images/delete.svg";
import { useNavigate } from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import debounce from "lodash.debounce";
import { useLoading } from '../context/LoadingContext';

const Bay = () => {
    const [bayNumber, setBayNumber] = useState('');
    const [shop, setShop] = useState('');
    const [searchShop, setSearchShop] = useState('');
    const [unloadingLocation, setUnloadingLocation] = useState('');
    const [searchUnloading, setSearchUnloading] = useState('');
    const [bayType, setBayType] = useState('');
    const [searchBayType, setSearchBayType] = useState('');
    const navigate = useNavigate();
    const { updateLoading } = useLoading();
    const [inputPage, setInputPage] = useState(1);
    const limit = 10; // items per page


    const [shops, setShops] = useState([]);
    const [unloadingLocations, setUnloadingLocations] = useState([]);
    const [bayTypes, setBayTypes] = useState([]);
    const [bayDetails, setBayDetails] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
    const [isUnloadDropdownOpen, setIsUnloadDropdownOpen] = useState(false);
    const [isBayTypeDropdownOpen, setIsBayTypeDropdownOpen] = useState(false);

    const [showShopFilter, setShowShopFilter] = useState(false);
    const [selectedShop, setSelectedShop] = useState('');
    const [shopOptions, setShopOptions] = useState([]);

    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedBayType, setSelectedBayType] = useState('');
    const [showLocationFilter, setShowLocationFilter] = useState(false);
    const [showBayTypeFilter, setShowBayTypeFilter] = useState(false);
    const [locationOptions, setLocationOptions] = useState(false);
    const [bayTypeOptions, setBayTypeOptions] = useState([]);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);


    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedBay, setSelectedBay] = useState(null);
    const [formData, setFormData] = useState({
        number: "",
        location_id: "",
        shop_id: "",
        bay_type_id: "",
    });
    const shopRef = useRef(null);
    const unloadRef = useRef(null);
    const bayTypeRef = useRef(null);
    const bayNumberRef = useRef(null);
    const fileInputRef = useRef(null);


    const dropdownShopRef = useRef(null);
    const dropdownUnloadRef = useRef(null);
    const dropdownBayTypeRef = useRef(null);
    const debouncedSearchRef = useRef();

    useEffect(() => {
        axiosInstance.get("/api/shop/shops")
            .then((response) => {
                setShops(response.data.shops);
                const uniqueShopNames = response.data.shops.map(item => item.shop_name).filter(Boolean);
                setShopOptions(uniqueShopNames);
            })

            .catch(error => console.error("Error fetching shops:", error));

        axiosInstance.get("/api/unloading-location")
            .then((response) => {
                setUnloadingLocations(response.data.locations)
                const uniqueLocationNames = response.data.locations.map(item => item.location_name).filter(Boolean);
                setLocationOptions(uniqueLocationNames);
            })
            .catch(error => console.error("Error fetching unloading locations:", error));

        axiosInstance.get("/api/bay-type")
            .then((response) => {
                setBayTypes(response.data.bayTypes)
                const uniqueBayTypeNames = response.data.bayTypes.map(item => item.type_name).filter(Boolean);
                setBayTypeOptions(uniqueBayTypeNames);
            })
            .catch(error => console.error("Error fetching bay types:", error));
    }, [updateLoading]);


    const fetchBayDetails = useCallback(async () => {
        updateLoading(true);

        try {
            const res = await axiosInstance.get("/api/bays/configurations", {
                params: {
                    limit: 1000,
                    number: searchTerm,
                },
            });

            const allBays = res.data.data.data;

            const filtered = allBays.filter(bay =>
                (!selectedShop || bay.shop?.shop_name === selectedShop) &&
                (!selectedLocation || bay.location?.location_name === selectedLocation) &&
                (!selectedBayType || bay.bayType?.type_name === selectedBayType)
            );

            const totalFilteredPages = Math.ceil(filtered.length / limit);
            let startIdx = (page - 1) * limit;
            let endIdx = startIdx + limit;
            let matchedBays = filtered.slice(startIdx, endIdx);

            // Handle zero filtered result regardless of current page
            if (filtered.length === 0) {
                setBayDetails([]);
                setTotalPages(0);
                setPage(1);
                setInputPage(1);

                updateLoading(false);
                return;
            }

            // Handle case where matchedBays is empty on the current page
            if (matchedBays.length === 0 && totalFilteredPages > 0) {
                let found = false;
                for (let i = 1; i <= totalFilteredPages; i++) {
                    if (i === page) continue;

                    const altSlice = filtered.slice((i - 1) * limit, i * limit);

                    if (altSlice.length > 0) {
                        setPage(i);
                        setInputPage(i);
                        found = true;
                        break;
                    }
                }

                if (!found) {
                    setBayDetails([]);
                    setTotalPages(0);
                    setPage(1);
                    setInputPage(1);
                    updateLoading(false);
                    return;
                }
            }


            setBayDetails(matchedBays);
            setTotalPages(totalFilteredPages);
            setInputPage(page);

        } catch (err) {
            console.error("Error fetching bays:", err);
            setBayDetails([]);
            setTotalPages(0);
            setPage(1);
            setInputPage(1);
        }

        updateLoading(false);
    }, [page, searchTerm, selectedShop, selectedLocation, selectedBayType, updateLoading]);

    useEffect(() => {
        debouncedSearchRef.current = debounce((value) => {
            setPage(1);
        }, 500);

        return () => {
            debouncedSearchRef.current.cancel();
        };
    }, []);

    useEffect(() => {
        if (debouncedSearchRef.current) {
            debouncedSearchRef.current(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        fetchBayDetails();
    }, [page, selectedShop, selectedLocation, selectedBayType, fetchBayDetails]);



    const handleSearchChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) { // Allow only digits (0-9)
            setSearchTerm(value);
            setPage(1); // Reset to page 1 on search change
        }
    };



    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleInputChange = (e) => {
        setInputPage(e.target.value);
    };

    const handleInputSubmit = () => {
        const newPage = Number(inputPage);
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        } else {
            setInputPage(page); // reset if invalid
        }
    };


    const handleCancel = () => {
        setBayNumber('');
        setShop('');
        setSearchShop('');
        setUnloadingLocation('');
        setSearchUnloading('');
        setBayType('');
        setSearchBayType('');
        setShowAddForm(false);
    };


    const handleAdd = async () => {
        if (!bayNumber || !shop || !unloadingLocation || !bayType) {
            Swal.fire("Error", "Please fill all fields", "error");
            return;
        }

        const formattedBayNumber = bayNumber.trim().toUpperCase();

        const requestData = {
            number: formattedBayNumber,
            shop_id: Number(shop),
            location_id: Number(unloadingLocation),
            bay_type_id: Number(bayType),
            status: "Registered"
        };

        try {
            await axiosInstance.post("/api/bays/create", requestData);
            Swal.fire("Success", "Bay details added successfully!", "success");
            handleCancel();
            fetchBayDetails();
        } catch (error) {
            const backendMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Failed to add bay details";

            console.error("Error adding bay details:", backendMessage);
            Swal.fire("Error", backendMessage, "error");
        }

    };

    const filteredShops = shops.filter(shop =>
        shop.shop_name.toLowerCase().includes(searchShop.toLowerCase())
    );


    const filteredUnloadingLocations = unloadingLocations.filter(loc =>
        loc.location_name.toLowerCase().includes(searchUnloading.toLowerCase())
    );

    const filteredBayTypes = bayTypes.filter(type =>
        type.type_name.toLowerCase().includes(searchBayType.toLowerCase())
    );

    const handleShopSelect = (id, name) => {
        setShop(id);
        setSearchShop(name);
        setIsShopDropdownOpen(false);
        unloadRef.current?.focus();
    };

    const handleUnloadingSelect = (id, name) => {
        setUnloadingLocation(id);
        setSearchUnloading(name);
        setIsUnloadDropdownOpen(false);
        bayTypeRef.current?.focus();
    };

    const handleBayTypeSelect = (id, name) => {
        setBayType(id);
        setSearchBayType(name);
        setIsBayTypeDropdownOpen(false);
        bayNumberRef.current?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownShopRef.current && !dropdownShopRef.current.contains(event.target)) {
                setIsShopDropdownOpen(false);
            }
            if (dropdownUnloadRef.current && !dropdownUnloadRef.current.contains(event.target)) {
                setIsUnloadDropdownOpen(false);
            }
            if (dropdownBayTypeRef.current && !dropdownBayTypeRef.current.contains(event.target)) {
                setIsBayTypeDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleUpdateBay = async () => {
        const { number, location_id, shop_id, bay_type_id } = formData;

        if (!number || !location_id || !shop_id || !bay_type_id) {
            Swal.fire("Error", "Please fill all fields", "error");
            return;
        }

        if (!selectedBay || !selectedBay.id) {
            Swal.fire("Error", "No bay selected for update", "error");
            return;
        }

        try {
            await axiosInstance.put(`/api/bays/${selectedBay.id}`, {
                number: number.trim(),
                location_id: Number(location_id),
                shop_id: Number(shop_id),
                bay_type_id: Number(bay_type_id),
            });

            setEditModalOpen(false);
            setSelectedBay(null);
            setFormData({
                number: "",
                location_id: "",
                bay_type_id: "",
                shop_id: "",
            });

            Swal.fire({
                icon: "success",
                title: "Bay updated successfully",
                confirmButtonText: "OK",
                allowOutsideClick: false,
            });

            fetchBayDetails();

        } catch (err) {
            console.error("Update error:", err);
            Swal.fire({
                icon: "error",
                title: "Error updating bay",
                text: err.response?.data?.message || err.message || "Something went wrong",
            });
        }
    };

    const isFormValid = searchShop && searchUnloading && searchBayType && bayNumber;


    const handleEditBay = (bay) => {
        setSelectedBay(bay);
        setFormData({
            number: bay.number || "",
            location_id: bay.location?.id || "",
            shop_id: bay.shop?.id || "",
            bay_type_id: bay.bayType?.id || "",
        });
        setEditModalOpen(true);
    };


    const handleUnloadInput = (e) => {
        const value = e.target.value;
        const match = locationOptions.find(loc => loc.toLowerCase().startsWith(value.toLowerCase()));

        if (!value || match) {
            setSearchUnloading(value);
            setIsUnloadDropdownOpen(true);
        } else {
            // Stop input if no match
            e.preventDefault(); // prevents typing invalid characters
        }
    };

    const handleShopInput = (e) => {
        const value = e.target.value;
        const match = shopOptions.find(shop => shop.toLowerCase().startsWith(value.toLowerCase()));

        if (!value || match) {
            setSearchShop(value);
            setIsShopDropdownOpen(true);
        } else {
            // Stop typing if no match
            e.preventDefault(); // prevents invalid characters
        }
    };


    const handleBayTypeInput = (e) => {
        const value = e.target.value;
        const match = bayTypeOptions.find(type => type.toLowerCase().startsWith(value.toLowerCase()));

        if (!value || match) {
            setSearchBayType(value);
            setIsBayTypeDropdownOpen(true);
        } else {
            // Stop typing if no match
            e.preventDefault(); // prevents invalid characters
        }
    };

    const handleFileUploadClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        console.log("Selected file:", file);
    };

    // Handle Upload button click
    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            // Call backend API
            const response = await axiosInstance.post("/api/bays/bay-upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            // Show success message from backend
            Swal.fire("Uploaded!", response.data.message, "success").then(() => {
                // Redirect to index page after closing the alert
            });

            // Reset file input
            setSelectedFile(null);
            fileInputRef.current.value = null;
        } catch (error) {
            console.error("Upload failed:", error);

            // Show error message
            Swal.fire(
                "Error!",
                error.response?.data?.error || "Failed to upload file",
                "error"
            );
        }
    };

    const handleDownloadFormat = () => {
        const dummyData = [
            { sl_no: "1", shop: "AS-KB", unloading_location: "K", bay_type: "Others", bay_number: "AS-16" },
            { sl_no: "2", shop: "PS-KB", unloading_location: "T", bay_type: "Others", bay_number: "AX-10" },
        ];
        const worksheet = XLSX.utils.json_to_sheet(dummyData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "BayFormat");
        XLSX.writeFile(workbook, "Bay_Format.xlsx");
    };


    const handleExportBays = async () => {
        try {
            const response = await axiosInstance.get("/api/bays/bay-export", {
                responseType: "blob", // important for file download
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Bay_Data.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // ✅ Show SweetAlert success
            Swal.fire({
                icon: "success",
                title: "Export Started",
                text: "File Downloading has Started!",
                confirmButtonText: "OK",
            });

        } catch (error) {
            console.error("Export failed:", error);

            // ✅ Show SweetAlert error
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.response?.data?.error || "Failed to export bay data",
                confirmButtonText: "OK",
            });
        }
    };



    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2 mt-4 me-4 ms-4">
                {/* Search bar on the left */}
                <div className="flex-grow-1 " style={{ maxWidth: "300px" }}>
                    <input
                        type="text"
                        className="form-control rounded-pill"
                        placeholder="Search by Bay Number"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => {
                            if (['e', 'E', '-', '+', ' '].includes(e.key)) {
                                e.preventDefault(); // Prevent non-numeric characters
                            }
                        }}
                    />
                </div>

                {/* Buttons on the right */}
                <div className="d-flex gap-2">
                    <button
                        className="btn"
                        style={{ backgroundColor: "#0A4595", color: "white" }}
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        <i className="bi bi-plus-lg me-1"></i>{showAddForm ? "Hide Form" : "Add Bay"}
                    </button>

                    <button
                        className="btn"
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        onClick={() => setShowBulkModal(true)}
                    >
                        <i className="bi bi-people-fill me-1"></i> Add in Bulk
                    </button>

                    <button className="btn btn-success" onClick={handleExportBays}>
                        <i className="bi bi-upload me-1"></i> Export
                    </button>
                </div>
            </div>


            {showAddForm && (
                <div className="card p-4 shadow-lg border-0 rounded-4 mb-5">
                    <h4 className="fw-bold text-center" style={{ color: "#0A4595" }}>Add Bay</h4>

                    {/* Single Row for All Fields */}
                    <div className="row g-3 mt-3 align-items-end">

                        {/* Shop */}
                        <div className="col-md-3 position-relative" ref={dropdownShopRef}>
                            <label className='form-label fw-bold'>Shop</label>
                            <input
                                ref={shopRef}
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Search or Select Shop"
                                value={searchShop}
                                onChange={handleShopInput}
                                onClick={() => setIsShopDropdownOpen(true)}
                                onKeyDown={(e) => e.key === "Enter" && unloadRef.current.focus()}
                            />
                            {isShopDropdownOpen && (
                                <div
                                    className="position-absolute w-100 shadow-sm border rounded p-2"
                                    style={{ backgroundColor: "white", zIndex: 10, maxHeight: "125px", overflowY: "auto" }}
                                >
                                    <ul className="list-unstyled mb-0">
                                        {filteredShops.length > 0 ? (
                                            filteredShops.map((s) => (
                                                <li key={s.id} className="dropdown-item mb-1" onClick={() => {
                                                    handleShopSelect(s.id, s.shop_name);
                                                    setSearchShop(s.shop_name);
                                                    setIsShopDropdownOpen(false);
                                                }}>
                                                    {s.shop_name}
                                                </li>
                                            ))
                                        ) : (
                                            <li className="dropdown-item text-muted">No shops found</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Unloading Location */}
                        <div className="col-md-3 position-relative" ref={dropdownUnloadRef}>
                            <label className='form-label fw-bold'>Unloading Location</label>
                            <input
                                ref={unloadRef}
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Search or Select Unloading Location"
                                value={searchUnloading}
                                onChange={handleUnloadInput}
                                onClick={() => setIsUnloadDropdownOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && bayTypeRef.current) {
                                        bayTypeRef.current.focus();
                                    }
                                }}
                            />

                            {isUnloadDropdownOpen && (
                                <div className="position-absolute w-100 bg-white shadow-sm border rounded p-2" style={{ zIndex: 10, maxHeight: "125px", overflowY: "auto" }}>
                                    <ul className="list-unstyled mb-0">
                                        {filteredUnloadingLocations.length > 0 ? (
                                            filteredUnloadingLocations.map((loc) => (
                                                <li
                                                    key={loc.id}
                                                    className="dropdown-item mb-1"
                                                    onClick={() => {
                                                        handleUnloadingSelect(loc.id, loc.location_name);
                                                        setSearchUnloading(loc.location_name); // set full value
                                                        setIsUnloadDropdownOpen(false);
                                                    }}
                                                >
                                                    {loc.location_name}
                                                </li>
                                            ))
                                        ) : (
                                            <li className="dropdown-item text-muted">No unloading locations found</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Bay Type */}
                        <div className="col-md-3 position-relative" ref={dropdownBayTypeRef}>
                            <label className='form-label fw-bold'>Bay Type</label>
                            <input
                                ref={bayTypeRef}
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Search or Select Bay Type"
                                value={searchBayType}
                                onChange={handleBayTypeInput}
                                onClick={() => setIsBayTypeDropdownOpen(true)}
                                onKeyDown={(e) => e.key === "Enter" && bayNumberRef.current.focus()} // jump to next
                            />

                            {isBayTypeDropdownOpen && (
                                <div className="position-absolute w-100 bg-white shadow-sm border rounded p-2" style={{ maxHeight: "125px", overflowY: "auto", zIndex: 10 }}>
                                    <ul className="list-unstyled mb-0">
                                        {filteredBayTypes.length > 0 ? (
                                            filteredBayTypes.map((type) => (
                                                <li
                                                    key={type.id}
                                                    className="dropdown-item mb-1"
                                                    onClick={() => {
                                                        handleBayTypeSelect(type.id, type.type_name);
                                                        setSearchBayType(type.type_name);
                                                        setIsBayTypeDropdownOpen(false);
                                                    }}
                                                >
                                                    {type.type_name}
                                                </li>
                                            ))
                                        ) : (
                                            <li className="dropdown-item text-muted">No bay types found</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Bay Number */}
                        <div className="col-md-3">
                            <label className='form-label fw-bold'>Bay Number</label>
                            <input
                                ref={bayNumberRef}
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Enter Bay Number"
                                value={bayNumber}
                                onChange={(e) => setBayNumber(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAdd()} // ⬅️ final enter = submit
                            />
                        </div>

                    </div>

                    {/* Buttons */}
                    <div className="d-flex justify-content-center mt-4 gap-3">
                        <button
                            className="btn"
                            style={{ backgroundColor: "#0A4595", color: "white" }}
                            onClick={handleAdd}
                            disabled={!isFormValid} // <-- disable if form not valid
                        >
                            Add
                        </button>
                        <button className="btn btn-secondary rounded-pill px-4" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>

                </div>
            )}


            <div className="table-responsive mx-4 mt-4">
                <table className="table table-sm">
                    <thead className="text-white">
                        <tr>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Sl. No.</th>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Bay Number</th>
                            <th className="text-center py-2 px-0 position-relative" style={{ backgroundColor: "#0A4595", minWidth: "200px", color: 'white' }}>
                                Shop Name
                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowShopFilter(!showShopFilter)} />
                                <br />
                                {showShopFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed" }}
                                    >
                                        {shopOptions.map((shopName) => (
                                            <div
                                                key={shopName}
                                                className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                                                onClick={() => {
                                                    setSelectedShop(shopName);
                                                    setShowShopFilter(false);
                                                }}
                                                style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                                {shopName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedShop && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedShop}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px" }} onClick={() => setSelectedShop("")} />
                                    </div>
                                )}
                            </th>

                            <th className="text-center text-white py-2 px-0 position-relative" style={{ backgroundColor: "#0A4595" }}>
                                Location Name
                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowLocationFilter(!showLocationFilter)} />
                                <br />
                                {showLocationFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed" }}
                                    >
                                        {locationOptions.map((location) => (
                                            <div
                                                key={location}
                                                className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                                                onClick={() => {
                                                    setSelectedLocation(location);
                                                    setShowLocationFilter(false);
                                                }}
                                                style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                                {location}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedLocation && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedLocation}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px" }} onClick={() => setSelectedLocation("")} />
                                    </div>
                                )}
                            </th>

                            <th className="text-center text-white py-2 px-0 position-relative" style={{ backgroundColor: "#0A4595" }}>
                                Bay Type
                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowBayTypeFilter(!showBayTypeFilter)} />
                                <br />
                                {showBayTypeFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed"  }}
                                    >
                                        {bayTypeOptions.map((type) => (
                                            <div
                                                key={type}
                                                className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                                                onClick={() => {
                                                    setSelectedBayType(type);
                                                    setShowBayTypeFilter(false);
                                                }}
                                                style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                                {type}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedBayType && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedBayType}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px" }} onClick={() => setSelectedBayType("")} />
                                    </div>
                                )}
                            </th>

                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bayDetails.length > 0 ? (
                            bayDetails
                                .filter(bay => selectedShop ? bay.shop?.shop_name === selectedShop : true)
                                .filter(bay => selectedLocation ? bay.location?.location_name === selectedLocation : true)
                                .filter(bay => selectedBayType ? bay.bayType?.type_name === selectedBayType : true)
                                .map((bay, index) => (
                                    <tr key={bay.id}>
                                        <td className="text-center p-2">{(page - 1) * 10 + index + 1}</td>
                                        <td className="text-center p-2">{bay.number}</td>
                                        <td className="text-center p-2">{bay.shop ? bay.shop.shop_name : "N/A"}</td>
                                        <td className="text-center p-2">{bay.location ? bay.location.location_name : "N/A"}</td>
                                        <td className="text-center p-2">{bay.bayType ? bay.bayType.type_name : "N/A"}</td>
                                        <td className="text-center p-2">
                                            <img
                                                src={View}
                                                alt="View"
                                                className="action-icon mx-2"
                                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                                title="View"
                                                onClick={() => navigate(`/bay/${bay.id}`)}
                                            />
                                            <img
                                                src={Edit}
                                                alt="Edit"
                                                className="action-icon mx-2"
                                                style={{ width: "18px", height: "18px" }}
                                                title="Edit"
                                                onClick={() => handleEditBay(bay)}

                                            />

                                            {editModalOpen && (
                                                <div
                                                    onClick={() => setEditModalOpen(false)}
                                                    style={{
                                                        position: "fixed",
                                                        inset: 0,
                                                        background: "rgba(0, 0, 0, 0.1)",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        zIndex: 9999,
                                                        padding: "20px",
                                                    }}
                                                >
                                                    <div
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            background: "#fff",
                                                            borderRadius: "16px",
                                                            width: "100%",
                                                            maxWidth: "600px",
                                                            padding: "24px",
                                                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.0)",
                                                            overflowY: "auto",
                                                            maxHeight: "90vh",
                                                            fontFamily: "'Inter', sans-serif",
                                                        }}
                                                    >
                                                        {/* Header */}
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                borderBottom: "1px solid #eee",
                                                                paddingBottom: "16px",
                                                                marginBottom: "24px",
                                                            }}
                                                        >
                                                            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#333" }}>
                                                                Edit Shop
                                                            </h2>
                                                            <button
                                                                onClick={() => setEditModalOpen(false)}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    fontSize: "22px",
                                                                    fontWeight: "bold",
                                                                    cursor: "pointer",
                                                                    color: "#999",
                                                                }}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>

                                                        {/* Form Inputs in One Row */}
                                                        <form
                                                            onSubmit={(e) => {
                                                                e.preventDefault();
                                                                handleUpdateBay();
                                                            }}
                                                            style={{
                                                                display: "flex",
                                                                gap: "20px",
                                                                flexWrap: "wrap",
                                                                marginBottom: "20px",
                                                            }}
                                                        >
                                                            {/* Bay Number */}
                                                            <div style={{ flex: 1, minWidth: "200px" }}>
                                                                <label
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        fontWeight: 600,
                                                                        marginBottom: "6px",
                                                                        display: "block",
                                                                        color: "#333",
                                                                        fontFamily: "Arial, sans-serif",
                                                                    }}
                                                                >
                                                                    Bay Number
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={formData.number}
                                                                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                                                    style={{
                                                                        padding: "10px",
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #ccc",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Arial, sans-serif",
                                                                        width: "100%",
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Location */}
                                                            <div style={{ flex: 1, minWidth: "200px" }}>
                                                                <label
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        fontWeight: 600,
                                                                        marginBottom: "6px",
                                                                        display: "block",
                                                                        color: "#333",
                                                                        fontFamily: "Arial, sans-serif",
                                                                    }}
                                                                >
                                                                    Location
                                                                </label>
                                                                <select
                                                                    className="form-control"
                                                                    value={formData.location_id}
                                                                    onChange={(e) => setFormData({ ...formData, location_id: Number(e.target.value) })}
                                                                    style={{
                                                                        padding: "10px",
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #ccc",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Arial, sans-serif",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <option value="">Select Location</option>
                                                                    {unloadingLocations.map((loc) => (
                                                                        <option key={loc.id} value={loc.id}>
                                                                            {loc.location_name}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                            </div>

                                                            {/* Shop */}
                                                            <div style={{ flex: 1, minWidth: "200px" }}>
                                                                <label
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        fontWeight: 600,
                                                                        marginBottom: "6px",
                                                                        display: "block",
                                                                        color: "#333",
                                                                        fontFamily: "Arial, sans-serif",
                                                                    }}
                                                                >
                                                                    Shop
                                                                </label>
                                                                <select
                                                                    className="form-control"
                                                                    value={formData.shop_id}
                                                                    onChange={(e) => setFormData({ ...formData, shop_id: e.target.value })}
                                                                    style={{
                                                                        padding: "10px",
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #ccc",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Arial, sans-serif",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <option value="">Select Shop</option>
                                                                    {shops.map((shop) => (
                                                                        <option key={shop.id} value={shop.id}>
                                                                            {shop.shop_name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            {/* Bay Type */}
                                                            <div style={{ flex: 1, minWidth: "200px" }}>
                                                                <label
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        fontWeight: 600,
                                                                        marginBottom: "6px",
                                                                        display: "block",
                                                                        color: "#333",
                                                                        fontFamily: "Arial, sans-serif",
                                                                    }}
                                                                >
                                                                    Bay Type
                                                                </label>
                                                                <select
                                                                    className="form-control"
                                                                    value={formData.bay_type_id}
                                                                    onChange={(e) => setFormData({ ...formData, bay_type_id: e.target.value })}
                                                                    style={{
                                                                        padding: "10px",
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #ccc",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Arial, sans-serif",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <option value="">Select Bay Type</option>
                                                                    {bayTypes.map((type) => (
                                                                        <option key={type.id} value={type.id}>
                                                                            {type.type_name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </form>


                                                        {/* Buttons */}
                                                        <div style={{ textAlign: "right" }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditModalOpen(false)}
                                                                style={{
                                                                    padding: "10px 16px",
                                                                    marginRight: "10px",
                                                                    backgroundColor: "#e0e0e0",
                                                                    color: "#333",
                                                                    border: "none",
                                                                    borderRadius: "6px",
                                                                    fontSize: "13px",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleUpdateBay}
                                                                style={{
                                                                    padding: "10px 20px",
                                                                    backgroundColor: 'rgb(10, 69, 149)',
                                                                    color: "#fff",
                                                                    border: "none",
                                                                    borderRadius: "6px",
                                                                    fontSize: "13px",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                Update
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}



                                            <img
                                                src={Delete}
                                                alt="Delete"
                                                className="action-icon mx-2"
                                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                                title="Delete"
                                            />
                                        </td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center p-3">No bays available</td>
                            </tr>
                        )}
                    </tbody>

                </table>
                <div className="d-flex justify-content-center align-items-center mt-4">
                    <button
                        className="btn btn-sm mx-2"
                        onClick={() => handlePageChange(page - 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={page === 1}
                    >
                        Previous
                    </button>

                    <div className="d-flex align-items-center mx-2">
                        <span className="mx-1">Page</span>
                        <input
                            type="number"
                            value={inputPage}
                            onChange={handleInputChange}
                            onBlur={handleInputSubmit}
                            onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
                            style={{ width: "60px", textAlign: "center" }}
                            min={1}
                            max={totalPages}
                        />
                        <span className="mx-1">of {totalPages}</span>
                    </div>

                    <button
                        className="btn btn-sm mx-2"
                        onClick={() => handlePageChange(page + 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>


            {showBulkModal && (
                <>
                    <div
                        className="position-relative d-flex flex-column align-items-center justify-content-center p-4"
                        style={{
                            top: "-300px",                   // center vertically
                            left: "50%",                  // center horizontally
                            transform: "translate(-50%, -50%)",
                            border: "2px dashed #ccc",
                            borderRadius: "10px",
                            width: "55%",
                            maxWidth: "800px",
                            textAlign: "center",
                            backgroundColor: "#fafafa",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            position: "relative",
                            zIndex: 1050,
                        }}
                    >
                        {/* Top right Download Format button */}
                        <button
                            className="btn btn-outline-primary position-absolute"
                            style={{ top: "15px", right: "20px", borderRadius: "20px", fontWeight: "500" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadFormat();
                            }}
                        >
                            <i className="bi bi-download me-2"></i> Download Format
                        </button>

                        <p className="h5 mb-2 mt-4 fw-bold" style={{ color: "#0A4595", fontWeight: "600" }}>
                            Select File Here
                        </p>

                        <p className="mb-4" style={{ color: "#6c757d", fontSize: "0.9rem" }}>
                            If you want to see which data is required, please download the format above.
                        </p>

                        {/* Hidden file input */}
                        <input
                            type="file"
                            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />

                        {/* Custom file box */}
                        <div className="d-flex align-items-center" style={{ width: "50%", minWidth: "220px", maxWidth: "250px" }}>
                            <div
                                className="flex-grow-1 p-1"
                                style={{ backgroundColor: "#d8d8d8ff", cursor: "pointer", border: "1px solid #9e9e9eff" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleFileUploadClick();
                                }}
                            >
                                <span className="text-truncate">Choose File</span>
                            </div>

                            <span
                                className="text-muted ms-2"
                                style={{ whiteSpace: "nowrap", fontSize: "0.9rem", alignSelf: "center" }}
                            >
                                {selectedFile ? selectedFile.name : "No file chosen"}
                            </span>
                        </div>

                        {/* File supported text with red star */}
                        <p className="mt-2" style={{ color: "#6c757d", fontSize: "0.9rem", fontStyle: "italic" }}>
                            <span style={{ color: "red" }}>*</span> Files Supported: CSV, XLS, XLSX
                        </p>

                        {/* Buttons: Upload and Cancel */}
                        <div className="d-flex gap-3 mt-3">
                            <button
                                className="btn"
                                style={{
                                    backgroundColor: "rgb(10, 69, 149)",
                                    color: "white",
                                    fontWeight: "500",
                                    borderRadius: "6px",
                                    width: "140px",
                                }}
                                onClick={handleUpload}
                                disabled={!selectedFile}
                            >
                                Upload
                            </button>

                            <button
                                className="btn btn-secondary"
                                style={{ fontWeight: "500", borderRadius: "6px", width: "140px" }}
                                onClick={() => setShowBulkModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>

                    {/* Overlay back ground */}
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 1040, backgroundColor: "rgba(0, 0, 0, 0.9)" }}
                        onClick={() => setShowBulkModal(false)}
                    ></div>
                </>
            )}


            {/* Overlay background for modal */}
            {/* {(showBulkModal) && (
                <div
                    className="modal-backdrop fade show"
                    onClick={() => {
                        setShowBulkModal(false);
                    }}
                ></div>
            )} */}

        </div>
    );
};

export default Bay;
